import type { ReactNode } from "react";

import { Navigate, useSearchParams } from "src/router";
import { getMetadataDrawerMode } from "src/cep/messages/editability";
import { useLocalizedLiveCampaignDraftBlock } from "src/cep/messages/hooks/use-message-localization-enabled";
import { useMessageOutletContextIfAvailable } from "src/cep/messages/message/outlet";
import { useMessageTypeConflict } from "src/cep/messages/use-message-type-conflict";
import { useCurrentSurface } from "src/cep/shared/use-current-message";

/** The full surface, not just `SurfaceEditability`: predicates that also key on
 *  the message itself (channel, say) must see the same data the drawer does. */
export type GuardedSurface = ReturnType<typeof useCurrentSurface>;

/** Why the content route refuses `surface`'s drafts: undefined while the localization setting loads, null when nothing blocks them. */
export function useContentRouteDraftBlock(
  surface: GuardedSurface,
): string | null | undefined {
  const messageCtx = useMessageOutletContextIfAvailable();
  return useLocalizedLiveCampaignDraftBlock({
    message: surface.message,
    content: surface.content,
    isJourneyMessage: messageCtx?.journeyState != null,
  });
}

/**
 * Sends the user back to the parent route when `isAllowed(surface)` is false.
 * Without `isAllowed`, the route needs editable content that isn't a localized live campaign's.
 * `?mode=preview` skips only the editable-content check, since other drawers have no read-only mode.
 */
export const DrawerRouteGuard = ({
  children,
  isAllowed,
}: {
  children: ReactNode;
  isAllowed?: (surface: GuardedSurface) => boolean;
}) => {
  const surface = useCurrentSurface();
  const [searchParams] = useSearchParams();
  const draftBlock = useContentRouteDraftBlock(surface);

  const isContentGate = isAllowed === undefined;
  const gate = isAllowed ?? ((s: GuardedSurface) => s.content.canEdit);
  const allowPreviewBypass =
    isContentGate && searchParams.get("mode") === "preview";

  // Checked before the preview bypass: a live campaign's preview still offers Design.
  if (isContentGate && draftBlock === undefined) return null;
  if (isContentGate && draftBlock) return <Navigate to=".." replace />;
  if (!gate(surface) && !allowPreviewBypass) {
    return <Navigate to=".." replace />;
  }
  return <>{children}</>;
};

/** The metadata route's guard, which waits to know whether a paused campaign has a message type to fix. */
export const MetadataDrawerRouteGuard = ({
  children,
}: {
  children: ReactNode;
}) => {
  const surface = useCurrentSurface();
  const hasMessageTypeConflict = useMessageTypeConflict({
    message: surface.message,
    enabled: surface.canEditSettings && !surface.isDraft,
  });
  if (hasMessageTypeConflict === undefined) return null;
  return (
    <DrawerRouteGuard
      isAllowed={(s) =>
        getMetadataDrawerMode(s, s.message.channel_type, {
          hasMessageTypeConflict,
        }) != null
      }
    >
      {children}
    </DrawerRouteGuard>
  );
};
